// Attack kinds and the read-game tuning constants.
// Design: souls-like commitment core (light/heavy/dodge/stamina) with a
// For-Honor-flavored read layer on top: parry beats attack, feint beats parry,
// kick beats block, attack beats kick.

import { PROFILES } from './profiles';
import type { ProfileId } from './profiles';

export type AttackKind = 'light' | 'heavy' | 'kick';

export interface AttackSpec {
  damage: number;
  staminaCost: number;
  windup: number;
  recovery: number;
  range: number;
  radius: number;
  arc: number;
}

/** Kick: barely any damage, exists purely to crush a held guard. */
export const KICK_SPEC: AttackSpec = {
  damage: 2,
  staminaCost: 12,
  windup: 0.28,
  recovery: 0.42,
  range: 1.35,
  radius: 0.5,
  arc: 70,
};

export function attackSpec(profile: ProfileId, kind: AttackKind): AttackSpec {
  const p = PROFILES[profile];
  if (kind === 'light') {
    return {
      damage: p.lightAttackDamage,
      staminaCost: p.lightAttackStaminaCost,
      windup: p.lightAttackWindup,
      recovery: p.lightAttackRecovery,
      range: p.lightAttackRange,
      radius: p.lightAttackRadius,
      arc: p.lightAttackArc,
    };
  }
  if (kind === 'heavy') {
    return {
      damage: Math.round(p.lightAttackDamage * 1.9),
      staminaCost: p.lightAttackStaminaCost * 1.55,
      windup: Math.max(0.42, p.lightAttackWindup * 2.6),
      recovery: p.lightAttackRecovery * 1.6,
      range: p.lightAttackRange + 0.15,
      radius: p.lightAttackRadius + 0.05,
      arc: Math.max(70, p.lightAttackArc - 15),
    };
  }
  return KICK_SPEC;
}

// Parry: blocking must have STARTED within this window when the hit lands.
export const PARRY_WINDOW = 0.15;
export const PARRIED_STUN = 1.2;

// Feint: cancel a heavy during the first part of its windup.
export const FEINT_STAMINA_COST = 6;
export const FEINT_WINDOW_FRACTION = 0.75;

// Dodge: short burst with invulnerability frames in the middle.
export const DODGE_STAMINA_COST = 14;
export const DODGE_DURATION = 0.38;
export const DODGE_IFRAME_START = 0.04;
export const DODGE_IFRAME_END = 0.26;
export const DODGE_SPEED_MULTIPLIER = 2.5;

// Heavies punish blocking twice as hard; kicks rip extra stamina on guard crush.
export const HEAVY_BLOCK_STAMINA_MULTIPLIER = 2;
export const KICK_GUARD_CRUSH_STAMINA = 15;
